import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Eye, Save, Loader2, Send } from 'lucide-react';
import certifiedTrainerService from '../../services/certifiedTrainer';
import { Lesson, CourseMediaItem } from '../../types';
import ConfirmModal from '../../components/common/ConfirmModal';
import OutlineNav from '../../components/course-edit/OutlineNav';
import BasicsForm from '../../components/course-edit/BasicsForm';
import MediaGalleryEditor from '../../components/course-edit/MediaGalleryEditor';
import LessonEditor from '../../components/course-edit/LessonEditor';
import PreviewModal from '../../components/course-edit/PreviewModal';
import { CourseBasics, OutlineSelection, PaymentCapabilities } from '../../components/course-edit/types';
import { newId, deriveThumbnail } from '../../components/course-edit/util';
import { useToast } from '../../hooks/useToast';
import { isFlagOn } from '../../utils/paymentFlags';

const emptyBasics: CourseBasics = {
  title: '', slug: '', description: '', duration: '', price: 0, currency: 'USD',
  category: '', startDate: '', startDateTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  accessDurationDays: 0, accessExpiresAt: '', stripeEnabled: true, qrPaymentEnabled: true, paymentProvider: 'platform',
  learningOutcomes: [],
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  published: 'bg-green-100 text-green-700',
};

export default function TeachingCourseEdit() {
  const { t } = useTranslation('teaching');
  const { courseId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gallerySaving, setGallerySaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingLessonId, setSavingLessonId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [actualCourseId, setActualCourseId] = useState(courseId);
  const [courseStatus, setCourseStatus] = useState<string>('draft');

  const [basics, setBasics] = useState<CourseBasics>(emptyBasics);
  const [gallery, setGallery] = useState<CourseMediaItem[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selection, setSelection] = useState<OutlineSelection>({ kind: 'basics' });

  const [basicsDirty, setBasicsDirty] = useState(false);
  const [galleryDirty, setGalleryDirty] = useState(false);
  const [dirtyLessonIds, setDirtyLessonIds] = useState<Set<string>>(new Set());

  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [paymentCapabilities, setPaymentCapabilities] = useState<PaymentCapabilities | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean; title: string; message: string; onConfirm: () => void; variant?: 'danger' | 'warning' | 'info'; confirmText?: string;
  } | null>(null);

  const isDirty = basicsDirty || galleryDirty || dirtyLessonIds.size > 0;
  const selectedLesson = selection.kind === 'lesson' ? lessons.find((l) => l._id === selection.id) ?? null : null;

  useEffect(() => {
    if (courseId) loadCourse(courseId);
    else autoCreateCourse();
    certifiedTrainerService.getPaymentCapabilities().then(setPaymentCapabilities).catch(() => {});
  }, [courseId]);

  // Warn before closing/refreshing the tab with unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  async function autoCreateCourse() {
    try {
      const newCourse = await certifiedTrainerService.createCourse({
        title: t('courseEdit.untitledCourse'), slug: 'untitled-course-' + Date.now(), description: '', status: 'draft',
      });
      navigate(`/my-teaching/courses/${newCourse._id}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || t('courseEdit.failedToCreateCourse'));
      setLoading(false);
    }
  }

  async function loadCourse(id: string) {
    try {
      const [coursesData, lessonsData] = await Promise.all([
        certifiedTrainerService.getCourses(),
        certifiedTrainerService.getLessons(id),
      ]);
      setExistingCategories([...new Set(coursesData.map((c: any) => c.category).filter(Boolean))]);
      const course = coursesData.find((c) => c._id === id);
      if (course) {
        setBasics({
          title: course.title, slug: course.slug, description: course.description,
          duration: course.duration, price: course.price, currency: course.currency || 'USD', category: course.category,
          startDate: course.startDate || '', startDateTimezone: course.startDateTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          accessDurationDays: course.accessDurationDays || 0, accessExpiresAt: course.accessExpiresAt || '',
          stripeEnabled: isFlagOn(course.stripeEnabled),
          qrPaymentEnabled: isFlagOn(course.qrPaymentEnabled),
          // Unset = Default (platform) Stripe, matching how checkout routes it.
          paymentProvider: course.paymentProvider === 'custom' ? 'custom' : 'platform',
          learningOutcomes: course.learningOutcomes || [],
        });
        // The course cover/thumbnail is now the gallery's first item. Seed the gallery from a
        // legacy thumbnail so existing courses keep their image (and it becomes editable here).
        const loadedGallery = course.galleryMedia || [];
        setGallery(loadedGallery.length === 0 && course.thumbnail
          ? [{ id: newId(), type: 'image', url: course.thumbnail }]
          : loadedGallery);
        setActualCourseId(id);
        setCourseStatus(course.status);
      }
      setLessons(lessonsData);
    } catch (err: any) {
      setError(err.response?.data?.error || t('courseEdit.failedToLoadCourse'));
    } finally {
      setLoading(false);
    }
  }

  function updateBasics(patch: Partial<CourseBasics>) {
    setBasics((prev) => ({ ...prev, ...patch }));
    setBasicsDirty(true);
  }

  // The gallery auto-saves on every change. Uploaded images/videos are written to storage
  // immediately, but the course→media link only lives in the DB once the course row is updated —
  // so without this an upload would vanish on a page refresh (before the user hits "Save Draft").
  // We persist just galleryMedia + the derived thumbnail, leaving any unsaved Basics edits alone.
  async function updateGallery(items: CourseMediaItem[]) {
    setGallery(items);
    if (!actualCourseId) { setGalleryDirty(true); return; }
    setGalleryDirty(true);
    setGallerySaving(true);
    try {
      await certifiedTrainerService.updateCourse(actualCourseId, {
        galleryMedia: items,
        thumbnail: deriveThumbnail(items),
      });
      setGalleryDirty(false);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courseEdit.failedToSaveCourse'));
    } finally {
      setGallerySaving(false);
    }
  }

  async function handleSaveCourse(andSubmit = false) {
    if (!basics.title.trim() || !basics.slug.trim()) {
      setSelection({ kind: 'basics' });
      toast.error(t('courseEdit.titleSlugRequired', { defaultValue: 'Title and URL slug are required' }));
      return;
    }
    setError('');
    if (andSubmit) setSubmitting(true); else setSaving(true);
    try {
      const courseData: any = {
        title: basics.title, slug: basics.slug, description: basics.description, thumbnail: deriveThumbnail(gallery),
        duration: basics.duration, price: basics.price, currency: basics.currency, category: basics.category,
        startDate: basics.startDate || undefined, startDateTimezone: basics.startDate ? basics.startDateTimezone : undefined,
        accessDurationDays: basics.accessDurationDays, accessExpiresAt: basics.accessExpiresAt,
        learningOutcomes: basics.learningOutcomes.map((s) => s.trim()).filter(Boolean),
        galleryMedia: gallery, status: 'draft' as const,
      };
      if (paymentCapabilities?.customPaymentEnabled) {
        courseData.stripeEnabled = basics.stripeEnabled;
        courseData.qrPaymentEnabled = basics.qrPaymentEnabled;
        courseData.paymentProvider = basics.paymentProvider;
      }
      const updated = await certifiedTrainerService.updateCourse(actualCourseId!, courseData);
      if (andSubmit) {
        await certifiedTrainerService.submitCourseForReview(actualCourseId!);
        setCourseStatus('pending');
        toast.success(t('courseEdit.courseSubmitted'));
      } else {
        // Reflect the saved status so the "Submit for review" button shows for draft/rejected.
        if (updated?.status) setCourseStatus(updated.status);
        toast.success(t('courseEdit.courseSavedAsDraft'));
      }
      setBasicsDirty(false);
      setGalleryDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.error || t('courseEdit.failedToSaveCourse'));
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  }

  async function handleAddLesson() {
    try {
      const newLesson = await certifiedTrainerService.createLesson(actualCourseId!, {
        title: t('courseEdit.untitledLesson', { defaultValue: 'Untitled Lesson' }), content: '', videoUrl: '', orderNum: lessons.length, duration: '',
      });
      setLessons((prev) => [...prev, newLesson]);
      setSelection({ kind: 'lesson', id: newLesson._id });
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courseEdit.failedToCreateLesson'));
    }
  }

  function updateSelectedLesson(patch: Partial<Lesson>) {
    if (selection.kind !== 'lesson') return;
    const id = selection.id;
    setLessons((prev) => prev.map((l) => (l._id === id ? { ...l, ...patch } : l)));
    setDirtyLessonIds((prev) => new Set(prev).add(id));
  }

  async function saveSelectedLesson() {
    if (!selectedLesson) return;
    setSavingLessonId(selectedLesson._id);
    try {
      await certifiedTrainerService.updateLesson(actualCourseId!, selectedLesson._id, selectedLesson);
      setDirtyLessonIds((prev) => { const next = new Set(prev); next.delete(selectedLesson._id); return next; });
      toast.success(t('courseEdit.lessonSaved', { defaultValue: 'Lesson saved' }));
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courseEdit.failedToSaveLesson'));
    } finally {
      setSavingLessonId(null);
    }
  }

  function handleDeleteLesson(lessonId: string) {
    setConfirmModal({
      isOpen: true, title: t('courseEdit.deleteLesson'), message: t('courseEdit.deleteLessonMessage'), variant: 'danger',
      onConfirm: async () => {
        try {
          await certifiedTrainerService.deleteLesson(actualCourseId!, lessonId);
          setLessons((prev) => prev.filter((l) => l._id !== lessonId));
          setDirtyLessonIds((prev) => { const next = new Set(prev); next.delete(lessonId); return next; });
          if (selection.kind === 'lesson' && selection.id === lessonId) setSelection({ kind: 'basics' });
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('courseEdit.failedToDeleteLesson'));
        }
        setConfirmModal(null);
      },
    });
  }

  async function handleReorderLessons(newOrder: Lesson[]) {
    const prevById = new Map(lessons.map((l) => [l._id, l.orderNum]));
    const reassigned = newOrder.map((l, i) => ({ ...l, orderNum: i }));
    setLessons(reassigned);
    const changed = reassigned.filter((l) => prevById.get(l._id) !== l.orderNum);
    try {
      await Promise.all(changed.map((l) => certifiedTrainerService.updateLesson(actualCourseId!, l._id, { orderNum: l.orderNum })));
    } catch {
      toast.error(t('courseEdit.reorderFailed', { defaultValue: 'Failed to save new order' }));
      try { setLessons(await certifiedTrainerService.getLessons(actualCourseId!)); } catch { /* ignore */ }
    }
  }

  function handleBack() {
    if (isDirty) {
      setConfirmModal({
        isOpen: true, title: t('courseEdit.unsavedChangesTitle', { defaultValue: 'Discard unsaved changes?' }),
        message: t('courseEdit.unsavedChangesMessage', { defaultValue: 'You have unsaved changes that will be lost.' }), variant: 'warning',
        confirmText: t('courseEdit.leave', { defaultValue: 'Leave' }),
        onConfirm: () => { setConfirmModal(null); navigate('/my-teaching/courses'); },
      });
    } else {
      navigate('/my-teaching/courses');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  const editor = (
    <>
      {selection.kind === 'basics' && (
        <BasicsForm value={basics} onChange={updateBasics} existingCategories={existingCategories} paymentCapabilities={paymentCapabilities} />
      )}
      {selection.kind === 'media' && (
        <MediaGalleryEditor items={gallery} onChange={updateGallery} courseId={actualCourseId} saving={gallerySaving} />
      )}
      {selection.kind === 'lesson' && (selectedLesson ? (
        <LessonEditor
          key={selectedLesson._id}
          lesson={selectedLesson}
          onChange={updateSelectedLesson}
          onSave={saveSelectedLesson}
          onDelete={() => handleDeleteLesson(selectedLesson._id)}
          saving={savingLessonId === selectedLesson._id}
          dirty={dirtyLessonIds.has(selectedLesson._id)}
        />
      ) : (
        <div className="glass-card rounded-lg p-8 text-center text-gray-500">{t('courseEdit.selectLesson', { defaultValue: 'Select a lesson to edit' })}</div>
      ))}
    </>
  );

  return (
    <div className="max-w-[1600px] mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" onClick={handleBack} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><ChevronLeft className="w-6 h-6" /></button>
          <h1 className="text-2xl font-bold text-gray-900 truncate">{t('courseEdit.editCourse')}</h1>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[courseStatus] || STATUS_BADGE.draft}`}>
            {t(`courseEdit.status${courseStatus.charAt(0).toUpperCase()}${courseStatus.slice(1)}`, { defaultValue: courseStatus })}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={() => setShowPreview(true)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 hover:bg-gray-100">
            <Eye className="w-4 h-4" />{t('courseEdit.preview', { defaultValue: 'Preview' })}
          </button>
          <button type="button" onClick={() => handleSaveCourse(false)} disabled={saving || submitting} className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-100 font-medium text-sm disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t('courseEdit.saveDraft')}
          </button>
          {(courseStatus === 'draft' || courseStatus === 'rejected') && (
            <button
              type="button"
              onClick={() => setConfirmModal({
                isOpen: true,
                title: t('courseEdit.submitConfirmTitle', { defaultValue: 'Submit for review?' }),
                message: t('courseEdit.submitConfirmMessage', { defaultValue: "Your course will be sent to the admin team for review. You won't be able to edit it while it's pending." }),
                variant: 'info',
                confirmText: t('courseEdit.saveAndSubmitReview'),
                onConfirm: () => { setConfirmModal(null); handleSaveCourse(true); },
              })}
              disabled={saving || submitting}
              className="inline-flex items-center gap-1.5 btn-gold px-4 py-2 text-sm disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{t('courseEdit.saveAndSubmitReview')}
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>}

      {/* 3-column workspace */}
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-6 lg:self-start">
          <OutlineNav
            selection={selection}
            onSelect={setSelection}
            lessons={lessons}
            galleryCount={gallery.length}
            basicsDirty={basicsDirty}
            galleryDirty={galleryDirty}
            dirtyLessonIds={dirtyLessonIds}
            onReorder={handleReorderLessons}
            onAddLesson={handleAddLesson}
          />
        </aside>

        <main className="flex-1 min-w-0">{editor}</main>
      </div>

      {/* Preview pop-up (Desktop / Mobile views) */}
      {showPreview && (
        <PreviewModal basics={basics} gallery={gallery} lessons={lessons} onClose={() => setShowPreview(false)} />
      )}

      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={confirmModal.confirmText || t('courseEdit.delete')}
        />
      )}
    </div>
  );
}
